import { useCallback, useEffect, useState } from 'react';
import { logError } from '../errors';

type StorageTuple<T> = [T, (next: T) => void];

/**
 * Just like useState but gets/sets the value in the browser's local storage.
 * 'key' should be a constant string. 'initialValue' is returned when local
 * storage does not have any data yet.
 *
 * @deprecated prefer `createUseStorageItem`, which also syncs values between consumers.
 */
export function useLocalStorage<T>(key: string, initialValue: T): StorageTuple<T> {
  const { value, setValueAndStore } = useStorage(window.localStorage, key, initialValue);
  return [value, setValueAndStore];
}

/**
 * Just like useState, but gets/sets the value in the browser's session storage.
 * 'key' should be a constant string. 'initialValue' is returned when session
 * storage does not have any data yet.
 *
 * @deprecated prefer `createUseStorageItem`, which also syncs values between consumers.
 */
export function useSessionStorage<T>(key: string, initialValue: T): StorageTuple<T> {
  const { value, setValueAndStore } = useStorage(window.sessionStorage, key, initialValue);
  return [value, setValueAndStore];
}

/**
 * Just like useLocalStorage, but syncs values across browser tabs.
 *
 * @deprecated prefer `createUseStorageItem`, which also syncs values within the same tab.
 */
export function useLocalStorageSynced<T>(key: string, initialValue: T): StorageTuple<T> {
  const { value, setValue, setValueAndStore } = useStorage<T>(window.localStorage, key, initialValue);

  useEffect(() => {
    // When local storage changes due to some other tab updating it, just
    // update our local state value
    const sync = (e: StorageEvent) => {
      if (e.key !== key || e.newValue === null) {
        return;
      }

      setValue(JSON.parse(e.newValue));
    };

    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('storage', sync);
    };
  }, [key, setValue]);

  return [value, setValueAndStore];
}

// Common functionality used by all storage hooks
function useStorage<T>(storage: Storage, key: string, initialValue: T) {
  const { getItem, setItem } = accessStorage<T>(storage, key);

  // Use state so that changes cause the page to re-render
  const [value, setValue] = useState<T>(() => {
    const storedValue = getItem();
    if (storedValue !== undefined) return storedValue;

    // Either the value isn't in storage yet or JSON parsing failed, so
    // set to the initial value in both places
    setItem(initialValue);
    return initialValue;
  });

  // Set in both places
  const setValueAndStore = useCallback(
    (val: T) => {
      setValue(val);
      trySetStorageItem(storage, key, val);
    },
    [setValue, storage, key]
  );

  return { value, setValue, setValueAndStore };
}

/**
 * @description
 * Hooks trigger re-renders, so use this function when you're solely
 * concerned with persisting state to storage and triggering a
 * re-render is a separate concern, for example handled by a containing
 * hook.
 */
export const accessStorage = <T>(storage: Storage, key: string) => {
  const getItem = (): T | undefined => {
    try {
      const jsonString = storage.getItem(key);
      // When undefined is stored to storage via JSON.parse, the returned
      // value is a string version of undefined
      if (jsonString !== null && jsonString !== 'undefined') {
        return JSON.parse(jsonString) as T;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const setItem = (value: T) => {
    trySetStorageItem(storage, key, value);
  };

  return { getItem, setItem };
};

/**
 * Wrapper around storage.setItem with a try/catch to avoid breaking the page
 * when something goes wrong with storage (e.g. hitting quota limits).
 */
export const trySetStorageItem = <T>(storage: Storage, key: string, value: T) => {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch (e) {
    const storageSizes = getAllStorageKeysAndSizes();
    // Catch and log if something goes wrong because we do not want to break
    // the page when something goes wrong with storage.
    // Include storage sizes in the error context for debugging
    logError(e, {
      contexts: {
        storage: {
          storageSizes,
        },
      },
    });
  }
};

/**
 * Gets all keys from a Storage object and returns their sizes in bytes.
 * @returns An array of objects containing key names and their corresponding sizes
 */
export const getStorageKeysAndSizes = (storage: Storage): { [key: string]: number } => {
  const result: { [key: string]: number } = {};
  const storageKeys = Object.keys(storage);

  try {
    storageKeys.forEach((key) => {
      const value = storage.getItem(key);
      // Calculate size in bytes (UTF-16 encoding, 2 bytes per character)
      const size = (value ?? '').length * 2;
      result[key] = size;
    });
  } catch (e) {
    // Catch and log if something goes wrong with accessing the Storage object
    logError(e);
  }

  // Get top 3 keys by size
  const sortedEntries = Object.entries(result)
    .sort(([, sizeA], [, sizeB]) => sizeB - sizeA) // Sort by size descending
    .slice(0, 3);

  return Object.fromEntries(sortedEntries);
};

/**
 * Gets storage information for both localStorage and sessionStorage.
 * @returns An object containing keys and sizes for both storage types
 */
export const getAllStorageKeysAndSizes = () => {
  return {
    localStorage: getStorageKeysAndSizes(window.localStorage),
    sessionStorage: getStorageKeysAndSizes(window.sessionStorage),
  };
};
